import type {
  FiltersResponse,
  DealsResponse,
  DealsQueryParams,
  FilterLookups,
  DashboardDeal,
  DashboardData,
  DealFromAPI,
} from "../types/api";
import { API_BASE_URL } from "../config";
import { normalizeCanton } from "../lib/utils";

/**
 * API Service
 *
 * Handles communication with backend API and transforms responses
 * into dashboard-ready format.
 *
 * Key responsibilities:
 * - Fetch filters (cantons, industries, phases, genders, year ranges)
 * - Fetch deals with optional filtering
 * - Build lookup maps (ID → human-readable name)
 * - Transform API deals to dashboard format
 * - Calculate amount ranges for visualization
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public statusText?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function fetchFilters(): Promise<FiltersResponse> {
  const url = `${API_BASE_URL}/api/v1/filters`;

  const response = await fetch(url);

  if (!response.ok) {
    console.error("✗ Filters failed");
    throw new ApiError(
      `Failed to fetch filters: ${response.status} ${response.statusText}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
}

export async function fetchDeals(params: DealsQueryParams = {}): Promise<DealsResponse> {
  const searchParams = new URLSearchParams();

  if (params.cantonIds) searchParams.append("cantonIds", params.cantonIds);
  if (params.phaseIds) searchParams.append("phaseIds", params.phaseIds);
  if (params.industryIds) searchParams.append("industryIds", params.industryIds);
  if (params.genderIds) searchParams.append("genderIds", params.genderIds);
  if (params.yearFrom) searchParams.append("yearFrom", params.yearFrom.toString());
  if (params.yearTo) searchParams.append("yearTo", params.yearTo.toString());

  const queryString = searchParams.toString();
  const url = `${API_BASE_URL}/api/v1/deals${queryString ? `?${queryString}` : ""}`;

  const response = await fetch(url);

  if (!response.ok) {
    console.error("✗ Deals failed");
    throw new ApiError(
      `Failed to fetch deals: ${response.status} ${response.statusText}`,
      response.status,
      response.statusText
    );
  }

  return response.json();
}

/**
 * Normalize industry name to consistent title case
 *
 * Handles special cases:
 * - ICT (all caps)
 * - MedTech, Biotech, Cleantech (specific casing)
 */
function normalizeIndustryName(name: string | undefined): string {
  if (!name) return "Unknown";

  return name
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .replace(/Ict/g, "ICT")
    .replace(/Biotech/gi, "Biotech")
    .replace(/Cleantech/gi, "Cleantech")
    .replace(/Medtech/gi, "MedTech")
    .replace(/Healthcare It/gi, "Healthcare It");
}

/**
 * Build lookup maps from filters for efficient ID → name conversion
 *
 * Transforms API filter metadata into fast lookup structures:
 * - Canton ID → Full canton name (e.g., "578" → "Aargau")
 * - Industry ID → Normalized name (e.g., "120" → "MedTech")
 * - Gender ID → Name
 * - Phase ID → Title
 */
export function buildFilterLookups(filters: FiltersResponse): FilterLookups {
  const cantonMap = new Map<string, string>();
  const industryMap = new Map<string, string>();
  const genderMap = new Map<string, string>();
  const phaseMap = new Map<string, string>();

  // Canton lookup: API codes → full Swiss names
  filters.cantons?.forEach((canton) => {
    const fullName = normalizeCanton(canton.name) || "Unknown";
    cantonMap.set(canton.id, fullName);
  });

  filters.industries?.forEach((industry) => {
    industryMap.set(industry.id, normalizeIndustryName(industry.name));
  });

  filters.genders?.forEach((gender) => {
    genderMap.set(gender.id, gender.name);
  });

  filters.phases?.forEach((phase) => {
    phaseMap.set(phase.id, phase.title);
  });

  return {
    cantonMap,
    industryMap,
    genderMap,
    phaseMap,
  };
}

/**
 * Categorize deal amount into display range
 *
 * Used for filtering and visualization grouping.
 * Returns "Unknown" for zero/missing amounts.
 */
function getAmountRange(amount: number): string {
  if (amount <= 0) return "Unknown";
  if (amount < 1) return "<1M";
  if (amount < 5) return "1-5M";
  if (amount < 10) return "5-10M";
  if (amount < 25) return "10-25M";
  if (amount < 50) return "25-50M";
  return "50M+";
}

/**
 * Transform API deals to Dashboard format
 *
 * Converts backend representation to UI-friendly structure:
 * - Replaces IDs with human-readable names via lookup maps
 * - Calculates amount ranges
 * - Adds convenience fields (HasAmount, VolumeMChf)
 * - Handles missing data gracefully (null → "Unknown")
 *
 * This transformation layer isolates the dashboard from API schema changes.
 */
export function transformDealsForDashboard(
  deals: DealFromAPI[],
  lookups: FilterLookups
): DashboardDeal[] {
  const { cantonMap, industryMap, genderMap, phaseMap } = lookups;

  return deals.map((deal) => {
    const amount = deal.amount || 0;
    const year = deal.year;
    const quarter: number | null = null;

    const canton = deal.cantonId ? cantonMap.get(deal.cantonId) || "Unknown" : "Unknown";
    const industry = deal.industryId ? industryMap.get(deal.industryId) || null : null;
    const genderCEO = deal.genderId ? genderMap.get(deal.genderId) || null : null;
    const phase = deal.phaseId ? phaseMap.get(deal.phaseId) || null : null;

    return {
      id: deal.id,
      Year: year,
      Quarter: quarter,
      Amount: amount,
      VolumeMChf: amount,
      Canton: canton,
      Industry: industry,
      Phase: phase,
      "Gender CEO": genderCEO,
      HasAmount: amount > 0,
      AmountRange: getAmountRange(amount),
    };
  });
}

/**
 * Load all dashboard data from API
 *
 * Orchestrates the complete data loading flow:
 * 1. Fetch filters to get year range metadata
 * 2. Calculate initial 13-year window
 * 3. Fetch deals for that window
 * 4. Build lookup maps
 * 5. Transform deals to dashboard format
 *
 * Returns complete dashboard data package ready for visualization.
 */
export async function loadDashboardData(): Promise<DashboardData> {
  // First, fetch filters to get available year range
  const filtersResponse = await fetchFilters();

  // Calculate initial year range: show last 13 years by default
  const yearTo = filtersResponse.years?.max ?? new Date().getFullYear();
  const yearFrom = filtersResponse.years?.min
    ? Math.max(filtersResponse.years.min, yearTo - 13)
    : yearTo - 13;

  // Fetch deals for the initial window
  const dealsResponse = await fetchDeals({
    yearFrom,
    yearTo,
  });

  const lookups = buildFilterLookups(filtersResponse);
  const transformedDeals = transformDealsForDashboard(dealsResponse.deals, lookups);

  return {
    deals: transformedDeals,
    filters: filtersResponse,
    lookups,
    meta: {
      total: dealsResponse.total,
      filtered: dealsResponse.filtered,
    },
  };
}

/**
 * Fetch filtered deals from API and transform
 *
 * Used for dynamic filtering operations after initial load.
 */
export async function fetchFilteredDeals(
  params: DealsQueryParams,
  lookups: FilterLookups
): Promise<{ deals: DashboardDeal[]; total: number; filtered: number }> {
  const dealsResponse = await fetchDeals(params);
  const transformedDeals = transformDealsForDashboard(dealsResponse.deals, lookups);

  return {
    deals: transformedDeals,
    total: dealsResponse.total,
    filtered: dealsResponse.filtered,
  };
}

const apiService = {
  fetchFilters,
  fetchDeals,
  buildFilterLookups,
  transformDealsForDashboard,
  loadDashboardData,
  fetchFilteredDeals,
  ApiError,
};

export default apiService;
